import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { disbursementsApi } from '../../api';
import {
  ITenantPayout,
  ITenantSettlement,
  ITenantPayoutsSummary,
  GetTenantPayoutsParams,
  GetTenantSettlementsParams,
  GetTenantPendingSettlementsParams,
} from '../../types';

interface IDisbursementsState {
  loading: boolean;
  payouts: ITenantPayout[];
  settlements: ITenantSettlement[];
  settlementsLoading: boolean;
  pendingSettlements: ITenantSettlement[];
  pendingSettlementsLoading: boolean;
  payoutsSummary: ITenantPayoutsSummary | null;
  summaryLoading: boolean;
  // Pagination state
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
}

const initialState: IDisbursementsState = {
  loading: false,
  payouts: [],
  settlements: [],
  settlementsLoading: false,
  pendingSettlements: [],
  pendingSettlementsLoading: false,
  payoutsSummary: null,
  summaryLoading: false,
  // Pagination initial state
  currentPage: 1,
  totalPages: 1,
  totalRecords: 0,
  pageSize: 10,
};

export const disbursementsSlice = createSlice({
  name: 'disbursements',
  initialState,
  reducers: {
    setPayoutsPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTenantPayouts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTenantPayouts.fulfilled, (state, { payload }) => {
      state.payouts = payload.data || [];
      state.currentPage = payload.page || 1;
      state.totalPages = Math.ceil(
        (payload.totalRecords || 0) / (payload.perPage || 10)
      );
      state.totalRecords = payload.totalRecords || 0;
      state.pageSize = payload.perPage || 10;
      state.loading = false;
    });
    builder.addCase(getTenantPayouts.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getTenantSettlements.pending, (state) => {
      state.settlementsLoading = true;
    });
    builder.addCase(getTenantSettlements.fulfilled, (state, { payload }) => {
      state.settlements = payload;
      state.settlementsLoading = false;
    });
    builder.addCase(getTenantSettlements.rejected, (state) => {
      state.settlementsLoading = false;
    });

    builder.addCase(getTenantPayoutsSummary.pending, (state) => {
      state.summaryLoading = true;
    });
    builder.addCase(getTenantPayoutsSummary.fulfilled, (state, { payload }) => {
      state.payoutsSummary = payload;
      state.summaryLoading = false;
    });
    builder.addCase(getTenantPayoutsSummary.rejected, (state) => {
      state.summaryLoading = false;
    });

    builder.addCase(getTenantPendingSettlements.pending, (state) => {
      state.pendingSettlementsLoading = true;
    });
    builder.addCase(
      getTenantPendingSettlements.fulfilled,
      (state, { payload }) => {
        state.pendingSettlements = payload;
        state.pendingSettlementsLoading = false;
      }
    );
    builder.addCase(getTenantPendingSettlements.rejected, (state) => {
      state.pendingSettlementsLoading = false;
    });
  },
});

const getTenantPayouts = createAsyncThunk(
  'getTenantPayouts',
  async (params?: GetTenantPayoutsParams) => {
    try {
      const { data } = await disbursementsApi.getTenantPayouts(params);

      // The API returns shape: { statusCode, data: ITenantPayout[], pagination: { totalRecords } }
      const payouts = data.data || [];
      const pagination = (data as any).pagination || {};

      const totalRecords = pagination.totalRecords || 0;
      const page = params?.page || 1;
      const perPage = params?.perPage || payouts.length || 10;

      return {
        data: payouts,
        totalRecords,
        page,
        perPage,
      };
    } catch (error: any) {
      throw error;
    }
  }
);

const getTenantSettlements = createAsyncThunk(
  'getTenantSettlements',
  async (params: GetTenantSettlementsParams) => {
    try {
      const { data } = await disbursementsApi.getTenantSettlements(params);
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getTenantPayoutsSummary = createAsyncThunk(
  'getTenantPayoutsSummary',
  async () => {
    try {
      const { data } = await disbursementsApi.getTenantPayoutsSummary();
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getTenantPendingSettlements = createAsyncThunk(
  'getTenantPendingSettlements',
  async (params?: GetTenantPendingSettlementsParams) => {
    try {
      const { data } = await disbursementsApi.getTenantPendingSettlements(
        params
      );
      const raw = (data as any).data;

      // Backend may return either a flat array or an object keyed by date/time.
      if (Array.isArray(raw)) {
        return raw as ITenantSettlement[];
      }

      if (raw && typeof raw === 'object') {
        // Flatten values from { [key]: ITenantSettlement[] }
        return Object.values(raw).flat() as ITenantSettlement[];
      }

      return [];
    } catch (error: any) {
      throw error;
    }
  }
);

export const disbursementsSelector = (state: RootState) => state.disbursements;
export {
  getTenantPayouts,
  getTenantSettlements,
  getTenantPendingSettlements,
  getTenantPayoutsSummary,
};
export const { setPayoutsPage } = disbursementsSlice.actions;
export default disbursementsSlice.reducer;
